// Printing a custom HTML Lens view.
//
// The view runs in an iframe sandboxed with `allow-scripts` only, so it cannot open the
// print dialog itself, and Admin cannot reach into it (it has an opaque origin). Printing is
// done in three steps, none of which gives the authored code a new capability:
//   1. Admin asks the view's bootstrap for a snapshot of the page as the reader sees it
//      (the bootstrap is Admin's code; the request carries the view's private channel).
//   2. Admin removes anything executable from that snapshot and locks it with a CSP that
//      allows no scripts and no network.
//   3. Admin prints the snapshot from a hidden, same-origin iframe sandboxed WITHOUT
//      `allow-scripts`, so nothing in it can run even if the clean-up missed something.

import { v4 as uuidv4 } from 'uuid';

export const SNAPSHOT_REQUEST = 'lens:snapshot-request';
export const SNAPSHOT_RESPONSE = 'lens:snapshot';
/** Larger snapshots are refused rather than printed slowly or partially. */
export const MAX_SNAPSHOT_CHARS = 8_000_000;
export const SNAPSHOT_TIMEOUT_MS = 5000;

/**
 * ES5 source added to the view's bootstrap. It copies the live state that cloning drops
 * (chosen options, ticked boxes, typed values, drawn canvases) into the clone as markup.
 */
export const SNAPSHOT_BOOTSTRAP = `
  function lensSnapshot() {
    var clone = document.documentElement.cloneNode(true);
    var live = document.querySelectorAll('input, select, textarea, canvas, img');
    var copies = clone.querySelectorAll('input, select, textarea, canvas, img');
    for (var i = 0; i < live.length && i < copies.length; i++) {
      var a = live[i], b = copies[i];
      if (a.tagName === 'SELECT') {
        for (var j = 0; j < a.options.length && j < b.options.length; j++) {
          if (a.options[j].selected) b.options[j].setAttribute('selected', ''); else b.options[j].removeAttribute('selected');
        }
      } else if (a.tagName === 'TEXTAREA') {
        b.textContent = a.value;
      } else if (a.tagName === 'CANVAS') {
        var img = document.createElement('img'), box = a.getBoundingClientRect();
        for (var k = 0; k < a.attributes.length; k++) { var attr = a.attributes[k]; if (attr.name !== 'width' && attr.name !== 'height') img.setAttribute(attr.name, attr.value); }
        img.src = a.toDataURL('image/png'); img.width = Math.round(box.width) || a.width; img.height = Math.round(box.height) || a.height;
        img.style.maxWidth = '100%'; img.style.height = 'auto'; img.style.aspectRatio = img.width + ' / ' + img.height; b.parentNode.replaceChild(img, b);
      } else if (a.tagName === 'IMG') {
        if (/^blob:/i.test(a.currentSrc || a.src)) {
          if (!a.complete || !a.naturalWidth) throw new Error('Image is not ready');
          var bitmap = document.createElement('canvas'); bitmap.width = a.naturalWidth; bitmap.height = a.naturalHeight;
          bitmap.getContext('2d').drawImage(a, 0, 0);
          b.removeAttribute('srcset'); b.src = bitmap.toDataURL('image/png');
        }
      } else if (a.type === 'checkbox' || a.type === 'radio') {
        if (a.checked) b.setAttribute('checked', ''); else b.removeAttribute('checked');
      } else {
        b.setAttribute('value', a.value);
      }
    }
    var nodes = document.documentElement.querySelectorAll('*'), twins = clone.querySelectorAll('*');
    for (var n = 0; n < nodes.length && n < twins.length; n++) {
      var node = nodes[n], css = getComputedStyle(node);
      if (/(auto|scroll)/.test(css.overflowY) && node.scrollHeight > node.clientHeight + 1) twins[n].setAttribute('data-lens-print-scroll', '');
    }
    if (document.body.scrollWidth > 1000) clone.setAttribute('data-lens-print-wide', '');
    return '<!doctype html>' + clone.outerHTML;
  }`;

/** The CSP the printed copy runs under: inline styles and data images only. */
export const PRINT_CSP =
  "default-src 'none'; script-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src 'none'; connect-src 'none'; media-src 'none'; object-src 'none'; frame-src 'none'; child-src 'none'; worker-src 'none'; base-uri 'none'; form-action 'none'";

// `template` also covers declarative shadow roots, which DOMParser leaves as templates but
// the print frame would attach (and whose contents querySelectorAll cannot see). SVG
// `animate` / `set` can retarget an href after sanitising, so they go too.
const REMOVED_ELEMENTS =
  'script, noscript, template, iframe, frame, frameset, object, embed, applet, fencedframe, portal, base, link, meta, animate, set';
const URL_ATTRIBUTES = ['href', 'src', 'xlink:href', 'action', 'formaction', 'poster', 'background', 'data', 'srcset'];

// Browsers ignore whitespace and control characters when reading a URL scheme, so they
// are dropped before the check ("java\nscript:" is still a script URL).
const normaliseUrl = (value: string) => {
  let normalised = '';
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code > 0x20 && (code < 0x7f || code > 0x9f)) {
      normalised += char;
    }
  }
  return normalised;
};

export const isScriptUrl = (value: string) => /^(?:javascript|vbscript):|^data:text\/html/i.test(normaliseUrl(value));

// Only raster images may stay as data: URLs (charts and copied canvases). Any other data:
// URL (SVG, XHTML, HTML) can carry script, so it is dropped wherever it appears.
const SAFE_DATA_URL = /^data:image\/(?:png|gif|jpe?g|webp|bmp|avif)[;,]/i;
export const isUnsafeUrl = (value: string) => {
  if (isScriptUrl(value)) {
    return true;
  }
  const trimmed = normaliseUrl(value);
  return /^data:/i.test(trimmed) && !SAFE_DATA_URL.test(trimmed);
};
// srcset holds several "url descriptor" candidates separated by commas. Whitespace also
// separates a URL from its descriptor, so "java\nscript:" splits into harmless-looking
// tokens; the whole value is therefore checked with whitespace removed as well.
const SAFE_DATA_TYPE = /^data:image\/(?:png|gif|jpe?g|webp|bmp|avif)$/i;
const isUnsafeSrcset = (value: string) => {
  const whole = normaliseUrl(value);
  if (/(?:javascript|vbscript):/i.test(whole)) {
    return true;
  }
  if ((whole.match(/data:[^,;]*/gi) ?? []).some((dataType) => !SAFE_DATA_TYPE.test(dataType))) {
    return true;
  }
  return value.split(',').some((candidate) => isUnsafeUrl(candidate.trim().split(/\s+/)[0] ?? ''));
};

/**
 * Turns a snapshot from the view into a document that cannot run anything: scripts,
 * frames, plugins, links and meta tags are removed, every `on*` handler and script URL is
 * stripped, and a no-script, no-network CSP is put first in <head>.
 */
export function sanitisePrintDocument(snapshot: string, title: string, fontCss = ''): string {
  const doc = new DOMParser().parseFromString(snapshot, 'text/html');
  doc.querySelectorAll(REMOVED_ELEMENTS).forEach((node) => node.remove());
  // Buttons do nothing on paper, and Chromium leaves a repeated table header row blank on
  // later pages when the row holds a <button> (a sortable column header, say). Print their
  // label as a span with the same attributes so the view's classes still style it.
  doc.querySelectorAll('button').forEach((button) => {
    const span = doc.createElement('span');
    Array.from(button.attributes).forEach((attribute) => {
      if (attribute.name.toLowerCase() !== 'type') {
        span.setAttribute(attribute.name, attribute.value);
      }
    });
    span.append(...Array.from(button.childNodes));
    button.replaceWith(span);
  });
  doc.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase();
      if (name.startsWith('on') || name === 'srcdoc' || name === 'formaction') {
        element.removeAttribute(attribute.name);
      } else if (
        URL_ATTRIBUTES.includes(name) &&
        (name === 'srcset' ? isUnsafeSrcset(attribute.value) : isUnsafeUrl(attribute.value))
      ) {
        element.removeAttribute(attribute.name);
      }
    });
  });
  const head = doc.head;
  const charset = doc.createElement('meta');
  charset.setAttribute('charset', 'utf-8');
  const csp = doc.createElement('meta');
  csp.setAttribute('http-equiv', 'Content-Security-Policy');
  csp.setAttribute('content', fontCss ? PRINT_CSP.replace("font-src 'none'", 'font-src data:') : PRINT_CSP);
  // A kept https link that is clicked must not tell the destination which Admin page it came from.
  const referrer = doc.createElement('meta');
  referrer.setAttribute('name', 'referrer');
  referrer.setAttribute('content', 'no-referrer');
  const titleElement = doc.createElement('title');
  titleElement.textContent = title;
  doc.querySelectorAll('title').forEach((node) => node.remove());
  head.prepend(charset, csp, referrer, titleElement);
  const motionStyle = doc.createElement('style');
  motionStyle.textContent = '*, *::before, *::after { animation: none !important; transition: none !important; }';
  head.append(motionStyle);
  const printStyle = doc.createElement('style');
  const hasPageRule = Array.from(doc.querySelectorAll('style')).some((style) =>
    /@page\b/i.test(style.textContent ?? ''),
  );
  // Important only for inherited viewport/scroller constraints that would otherwise crop paper.
  printStyle.textContent = `${hasPageRule ? '' : `@page { size: A4 ${doc.documentElement.hasAttribute('data-lens-print-wide') ? 'landscape' : 'portrait'}; margin: 6mm; }`}
    @media print {
      html, body { height: auto; min-height: 0; margin: 0; }
      [data-lens-print-scroll] { height: auto !important; max-height: none !important; overflow: visible !important; }
      thead { display: table-header-group; } tfoot { display: table-footer-group; }
      tr { break-inside: avoid; } h1, h2, h3 { break-after: avoid; }
      img { max-width: 100%; height: auto; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }`;
  head.append(printStyle);
  if (fontCss) {
    const fonts = doc.createElement('style');
    fonts.textContent = fontCss;
    head.append(fonts);
  }
  return `<!doctype html>${doc.documentElement.outerHTML}`;
}

type SnapshotMessage = { type: string; channel: string; requestId: string; html: unknown; error?: unknown };

/** Asks the view for its snapshot over its private channel. Rejects on silence or misuse. */
export function requestCustomViewSnapshot(
  frame: HTMLIFrameElement,
  channel: string,
  timeoutMs = SNAPSHOT_TIMEOUT_MS,
  signal?: AbortSignal,
): Promise<string> {
  const target = frame.contentWindow;
  if (!target) {
    return Promise.reject(new Error('The custom view is not loaded yet. Try again in a moment.'));
  }
  const requestId = uuidv4();
  return new Promise((resolve, reject) => {
    const finish = (error: Error | null, html?: string) => {
      window.clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      signal?.removeEventListener('abort', onAbort);
      if (error) {
        reject(error);
      } else {
        resolve(html);
      }
    };
    const onMessage = (event: MessageEvent<SnapshotMessage>) => {
      const data = event.data;
      if (
        event.source !== target ||
        data?.type !== SNAPSHOT_RESPONSE ||
        data.channel !== channel ||
        data.requestId !== requestId
      ) {
        return;
      }
      if (data.error === true) {
        finish(new Error('The view could not be copied for printing. A chart or image may not be ready.'));
      } else if (typeof data.html !== 'string' || !data.html) {
        finish(new Error('The custom view sent an empty page to print.'));
      } else if (data.html.length > MAX_SNAPSHOT_CHARS) {
        finish(new Error('This custom view is too large to print.'));
      } else {
        finish(null, data.html);
      }
    };
    const onAbort = () => finish(new Error('The view changed while preparing print. Try again.'));
    const timer = window.setTimeout(
      () => finish(new Error('The custom view did not respond. Reload the Lens and try again.')),
      timeoutMs,
    );
    window.addEventListener('message', onMessage);
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) {
      onAbort();
      return;
    }
    try {
      target.postMessage({ type: SNAPSHOT_REQUEST, channel, requestId }, '*');
    } catch {
      finish(new Error('The custom view could not be reached. Reload the Lens and try again.'));
    }
  });
}

/** How long a printed frame may linger if the browser never fires `afterprint`. */
export const PRINT_FRAME_FALLBACK_MS = 10 * 60 * 1000;

// The frame being prepared (locks out a second print), and frames already handed to the
// print dialog, which stay until `afterprint` so the dialog never loses its document.
let activePrintFrame: HTMLIFrameElement | null = null;
const printedFrames = new Map<HTMLIFrameElement, () => void>();

/**
 * Prints a sanitised document from a hidden iframe that is same-origin (so Admin can call
 * print on it) but sandboxed without scripts. The frame is removed after printing.
 */
export function printDocumentInHiddenFrame(
  html: string,
  ownerDocument: Document = document,
  signal?: AbortSignal,
): Promise<void> {
  if (activePrintFrame) {
    return Promise.reject(new Error('Another print preview is still being prepared. Try again in a moment.'));
  }
  // Any earlier print dialog has closed by now (it is modal), so its frame can go.
  printedFrames.forEach((cleanUp) => cleanUp());
  printedFrames.clear();
  const frame = ownerDocument.createElement('iframe');
  activePrintFrame = frame;
  frame.setAttribute('sandbox', 'allow-same-origin allow-modals');
  frame.setAttribute('aria-hidden', 'true');
  frame.setAttribute('tabindex', '-1');
  frame.title = 'Print preview';
  frame.setAttribute('style', 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;visibility:hidden');
  return new Promise((resolve, reject) => {
    let settled = false;
    let fallback = 0;
    let removed = false;
    let printView: Window | null = null;
    const remove = () => {
      removed = true;
      printView?.removeEventListener('afterprint', remove);
      window.clearTimeout(fallback);
      frame.remove();
      printedFrames.delete(frame);
      if (activePrintFrame === frame) {
        activePrintFrame = null;
      }
    };
    const finish = (error?: Error) => {
      if (settled) {
        return;
      }
      settled = true;
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', onAbort);
      frame.onload = null;
      frame.onerror = null;
      if (error) {
        remove();
        reject(error);
      } else {
        // print() can return before the dialog has rendered the document (it does in
        // headless Chromium), so the frame stays until the dialog closes.
        activePrintFrame = null;
        // Some browsers fire afterprint synchronously inside print().
        if (!removed) {
          printedFrames.set(frame, remove);
          fallback = window.setTimeout(remove, PRINT_FRAME_FALLBACK_MS);
        }
        resolve();
      }
    };
    const onAbort = () => finish(new Error('The view changed while preparing print. Try again.'));
    const timer = window.setTimeout(() => finish(new Error('The print preview did not load. Try again.')), 5000);
    signal?.addEventListener('abort', onAbort, { once: true });
    if (signal?.aborted) {
      onAbort();
      return;
    }
    frame.onerror = () => finish(new Error('The print preview could not be loaded.'));
    frame.onload = async () => {
      try {
        const view = frame.contentWindow;
        if (!view) {
          throw new Error('The print frame did not open.');
        }
        await view.document.fonts?.ready;
        if (signal?.aborted || removed) throw new Error('The view changed while preparing print. Try again.');
        printView = view;
        view.addEventListener('afterprint', remove, { once: true });
        view.focus();
        view.print();
        finish();
      } catch (error) {
        finish(error instanceof Error ? error : new Error('The browser blocked printing.'));
      }
    };
    try {
      frame.srcdoc = html;
      ownerDocument.body.appendChild(frame);
    } catch {
      finish(new Error('The print preview could not be loaded.'));
    }
  });
}
